export interface SsePostPayload<TMsg> {
  rawLine: string
  data: TMsg
}

export interface SsePostOptions<TBody extends Record<string, unknown>, TMsg = unknown> {
  url: string
  body: TBody
  headers?: Record<string, string>
  signal?: AbortSignal
  parseLine?: (line: string) => TMsg | undefined
  onMessage?: (payload: SsePostPayload<TMsg>) => void
  onError?: (err: Error) => void
  onComplete?: () => void
}

function defaultParseLine (line: string): unknown {
  const trimmed = line.trim()
  if (!trimmed.startsWith('data:')) {
    return undefined
  }
  const json = trimmed.replace(/^data:\s*/, '')
  if (!json) {
    return undefined
  }
  try {
    return JSON.parse(json) as unknown
  } catch {
    return undefined
  }
}

function normalizeNewlines (s: string): string {
  return s.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
}

export async function ssePost<TBody extends Record<string, unknown>, TMsg = unknown> (
  options: SsePostOptions<TBody, TMsg>
): Promise<void> {
  const {
    url,
    body,
    headers = {},
    signal,
    parseLine = defaultParseLine as (line: string) => TMsg | undefined,
    onMessage,
    onError,
    onComplete
  } = options

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...headers
      },
      body: JSON.stringify(body),
      signal
    })

    if (!res.ok) {
      onError?.(new Error(`HTTP ${res.status}`))
      return
    }

    if (!res.body) {
      onError?.(new Error('响应无 body'))
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    const emitCompleteLines = (): void => {
      buffer = normalizeNewlines(buffer)
      let idx = buffer.indexOf('\n')
      while (idx >= 0) {
        const line = buffer.slice(0, idx)
        buffer = buffer.slice(idx + 1)
        if (line.trim()) {
          const data = parseLine(line)
          if (data !== undefined) {
            onMessage?.({ rawLine: line, data })
          }
        }
        idx = buffer.indexOf('\n')
      }
    }

    while (true) {
      const { value, done } = await reader.read()
      if (value && value.length) {
        buffer += decoder.decode(value, { stream: true })
        emitCompleteLines()
      }
      if (done) {
        break
      }
    }

    buffer += decoder.decode()
    emitCompleteLines()
    if (buffer.trim()) {
      const line = buffer
      const data = parseLine(line)
      if (data !== undefined) {
        onMessage?.({ rawLine: line, data })
      }
    }

    onComplete?.()
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e))
    if (err.name === 'AbortError') {
      onComplete?.()
      return
    }
    onError?.(err)
  }
}
